// @ts-nocheck
/* eslint-disable no-extend-native */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, memo, useRef } from "react";
import NftPreview from "@mint/NftPreview";
import MintFooter from "@mint/MintFooter";
import Catalogue from "@mint/Catalogue";
import Name from "@mint/Name";
import Distribution from "@mint/Distribution";
import PieDist from "@mint/PieDist";
import FinalCheck from "@mint/FinalCheck";
import { fundRaisin } from "@contracts/fundraisin";
import Spinner from "@common/Spinner";
import { useNavigate } from "react-router-dom";
import error from "@config/error.json";
import { logError, logInfo } from "@utils/log";
import Modal from "@modals/Modal";
import Popup from "@modals/Popup";
import constant from "@utils/constant";
import { v4 as uuidv4 } from "uuid";
import { addLayerChoiceDynamically } from "@utils/common";
import { useAppContext } from "@context/AppContext";
import MintPopup from "@modals/MintPopup";
import "@assets/css/mint-loading.css";
import { useApiClient } from "@hooks/useApiClient";
import { parseErrorMessage } from "../../utils/errorUtils";

/**
 * Mint Component - Main NFT minting interface
 * Handles step-by-step NFT creation process with real-time preview
 */
const Mint = memo(() => {
  const {
    themeConfig,
    siteDetails,
    walletAddress,
    connectWallet,
    isWalletConnected,
    refreshNftData,
    constants,
    setError
  } = useAppContext();
  const { baseURL, get, post } = useApiClient()

  const navigate = useNavigate();

  // UI State
  const [isLoading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [stepState, setStep] = useState(0);
  const [showMintPopup, setShowMintPopup] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Data State
  const [backgroundColor, setBackgroundColor] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [layers, setLayers] = useState([]);
  const [names, setNames] = useState([]);
  const [data, setData] = useState([]);
  const [rarityList, setRarityList] = useState([]);
  const [presetDistribution, setPresetDistribution] = useState([]);

  // Cache for API responses - use refs to avoid dependency issues
  const dataCacheRef = useRef(new Map());
  const previewCacheRef = useRef(new Map());

  // Selection State
  const [selectedItems, setSelectedItems] = useState({});
  const [sliders, setDefaultSlider] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [preset, setPreset] = useState("");
  const [showPreset, setShowPreset] = useState(true);

  // Step Configuration
  const [identifier, setIdentifier] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [previewLink, setPreviewLink] = useState("");
  const [removedSteps, setRemovedSteps] = useState({});
  const [layerWiseChoice, setLayerWiseChoice] = useState({});
  const [previewQuery, setPreviewQuery] = useState();
  const [layerChoicesAreSingle, setLayerChoicesAreSingle] = useState(0);
  const [delayedName, setDelayName] = useState("");
  const [mr, setMr] = useState({});

  // Refs to prevent unnecessary re-renders
  const selectedItemsRef = useRef(selectedItems);
  const artOptionsRef = useRef(null);

  // Update ref when selectedItems changes
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  // Memoized rarity data structure
  const [rarityData, setRarityData] = useState(() => ({
    background: {},
    pattern: {},
    layer: {},
    layerchoice: {},
    layerclass: {},
    layerstylecolor: {},
    name: {},
  }));

  // Memoized art options configuration - stable reference
  const artOptionstemp = useMemo(() => [
    {
      title: constant.BACKGROUND_COLOR,
      identifier: "background",
      category: "background",
      widget: "catalogue",
      preview: true,
      navButton: constant.BACKGROUND_COLOR,
      getData: async function () {
        try {
          if (backgroundColor.length === 0) {
            const bg = await get(`/artCreationPack/${siteDetails.artCreationPackId}/backgrounds`);
            setBackgroundColor(bg);
            setData([...bg]);
          } else {
            setData([...backgroundColor]);
          }
          setLoading(false);
        } catch (err) {
          logError(err);
          setLoading(false);
        }
      },
    },
    {
      title: constant.PATTERN,
      identifier: "pattern",
      category: "pattern",
      widget: "catalogue",
      preview: true,
      navButton: constant.PATTERN,
      getData: async function () {
        try {
          if (patterns.length === 0) {
            const pattern = await get(`/artCreationPack/${siteDetails.artCreationPackId}/patterns`);
            setPatterns(pattern);
            setData([...pattern]);
          } else {
            setData([...patterns]);
          }
          setLoading(false);
        } catch (err) {
          logError(err);
          setLoading(false);
        }
      },
    },
    {
      title: constant.NAME,
      identifier: "name",
      category: "name",
      widget: "name",
      preview: false,
      navButton: constant.NAME,
      getData: async function () {
        try {
          if (names.length === 0) {
            const namesList = await get(`/artCreationPack/${siteDetails.artCreationPackId}/approvednames`);
            setNames(namesList);
          }
          setLoading(false);
        } catch (err) {
          logError(err);
          setLoading(false);
        }
      },
    },
    {
      title: constant.PICK_DISTRO,
      identifier: "distribution",
      category: "distribution",
      widget: "distribution",
      preview: false,
      navButton: constant.ALTER_EGO,
      getData: async function () {
        try {
          if (sliders.length === 0) {
            setDefaultSlider(siteDetails.sliders);
            const presets = await get(`/site/${siteDetails.siteId}/presetdistribution`);
            setPresetDistribution(presets);
          }
          setLoading(false);
        } catch (err) {
          logError(err);
          setLoading(false);
        }
      },
    },
    {
      title: constant.FINAL_TEXT,
      identifier: "confirmation",
      category: "confirmation",
      navButton: constant.FINAL_CHECK,
      widget: "final",
      preview: false,
      getData: function () {
        setLoading(false);
      },
    },
  ], []); // Empty dependency array - these options don't change

  const [artOptions, setArtOptions] = useState(() => [...artOptionstemp]);

  // Update ref when artOptions changes
  useEffect(() => {
    artOptionsRef.current = artOptions;
  }, [artOptions]);

  // Memoized functions for better performance
  const getRarity = useCallback(async () => {
    try {
      const cacheKey = `rarity-${siteDetails.siteId}`;
      if (dataCacheRef.current.has(cacheKey)) {
        setRarityList(dataCacheRef.current.get(cacheKey));
        return;
      }

      const rarity = await get(`/site/${siteDetails.siteId}/raritydetails`);
      setRarityList(rarity);
      dataCacheRef.current.set(cacheKey, rarity);
      logInfo("get rarity info", rarity);
    } catch (err) {
      logError("Failed to get rarity:", err);
    }
  }, [siteDetails.siteId]);

  const getPresetDistributions = useCallback(async () => {
    try {
      if (Object.keys(siteDetails).length > 0) {
        const cacheKey = `preset-${siteDetails.siteId}`;
        if (dataCacheRef.current.has(cacheKey)) {
          const cachedData = dataCacheRef.current.get(cacheKey);
          setPresetDistribution(cachedData.presets);
          if (sliders.length === 0) {
            setDefaultSlider(siteDetails.sliders);
          }
          if (cachedData.presets.length === 0) {
            setShowPreset(false);
          }
          return;
        }

        if (sliders.length === 0) {
          setDefaultSlider(siteDetails.sliders);
        }
        const presetExists = await get(`/site/${siteDetails.siteId}/presetdistribution`);
        if (presetExists.length === 0) {
          setShowPreset(false);
        }
        setPresetDistribution(presetExists);
        dataCacheRef.current.set(cacheKey, { presets: presetExists });
      }
    } catch (err) {
      logError("Failed to get preset distributions:", err);
    }
  }, [siteDetails, sliders]);

  const checkSiteActive = useCallback(async () => {
    try {
      const cacheKey = "site-active";
      if (dataCacheRef.current.has(cacheKey)) {
        const cachedResult = dataCacheRef.current.get(cacheKey);
        if (Date.now() - cachedResult.timestamp < 30000) { // Cache for 30 seconds
          return;
        }
      }

      const siteActive = await get(`/site/approved`);
      dataCacheRef.current.set(cacheKey, {
        result: siteActive,
        timestamp: Date.now()
      });

      if (!siteActive[0]) {
        let errorMsg = "";
        const { startsOn } = siteDetails;

        if (siteActive[1] === "SNA") {
          if (Math.floor(Date.now() / 1000) - startsOn >= 30) {
            errorMsg = error[siteActive[1]];
          } else if (Math.floor(Date.now() / 1000) - startsOn <= 0) {
            errorMsg = `Minting begins ${new Date(startsOn * 1000)}`;
          }
        } else {
          errorMsg = error[siteActive[1]];
        }
        setError(errorMsg);
      }
    } catch (err) {
      logError("Failed to check site active:", err);
    }
  }, [siteDetails, setError]);

  const validatePatternAndLayers = useCallback(() => {
    const allLayers = layers.filter(
      (layer) => layer.patternId === selectedItems?.pattern?.patternId
    );
    return allLayers.length === layers.length;
  }, [layers, selectedItems?.pattern?.patternId]);

  const validateSelection = useCallback(() => {
    if (artOptions[stepState].category === "pattern") {
      if (artOptions[stepState + 1]?.category === "layerchoice") {
        return validatePatternAndLayers();
      } else {
        if (
          layerChoicesAreSingle > 0 &&
          layerChoicesAreSingle === layers.length
        ) {
          return true;
        } else if (selectedItems[artOptions[stepState].identifier] && layers.length === 0) {
          return true;
        }
        return false;
      }
    } else if (selectedItems[artOptions[stepState].identifier]) {
      return true;
    }
    return false;
  }, [artOptions, stepState, validatePatternAndLayers, layerChoicesAreSingle, layers.length, selectedItems]);

  const getPreviewData = useCallback(() => {
    const data = [];
    for (const choice in selectedItems) {
      const { category } = selectedItems[choice];
      if (category === "background") {
        data.push({
          value: selectedItems[choice].colorHexCode,
          type: "color",
          class: "background",
        });
      } else if (category === "pattern") {
        data.push({
          value: selectedItems[choice].patternURL,
          type: "layer",
          class: "patterns",
          name: `pattern-${selectedItems[choice].patternId}`,
        });
      } else if (category === "layerchoice") {
        data.push({
          value: selectedItems[choice].layerURL,
          type: "layer",
          class: "patterns",
          name: `layer-${selectedItems[choice].layerChoiceId}`,
        });
      }
    }
    return data;
  }, [selectedItems]);

  // Helper function for debouncing
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  const preview = useCallback(async () => {
    console.log('Preview called with selectedItems:', Object.keys(selectedItems));

    try {
      const previewData = getPreviewData();
      console.log('Preview data:', previewData);

      if (previewData.length === 0) {
        console.log('No preview data, skipping preview');
        setLoader(false);
        setLoading(false);
        return;
      }
      setLoader(true);
      setLoading(true);

      // Cancel previous request
      if (previewQuery?.cancel) {
        previewQuery.cancel();
      }

      const body = { data: previewData, file: uuidv4() };
      const q = post(`/preview`, body, { responseType: 'blob' });
      setPreviewQuery(q);

      const blob = await q;
      console.log('Preview successful');


      setLoader(false);
      setLoading(false);
      setPreviewLink(URL.createObjectURL(blob));
    } catch (err) {
      console.error("Preview failed:", err);
      logError("Preview failed:", err);
      setLoader(false);
      setLoading(false);
    }
  }, [getPreviewData]);

  // Debounced preview function
  const debouncedPreview = useCallback(
    debounce(preview, 300),
    [preview, debounce]
  );

  const constructMintData = useCallback(() => {
    const nftMetadata = {};
    const attributes = [];

    nftMetadata.name = `${selectedItems.name.approvedName} #${selectedItems.name.nextNameNumber}`;
    nftMetadata.description = themeConfig.APP_DESC;
    nftMetadata.presetDistribution = selectedItems.distribution;
    nftMetadata.price = selectedItems.background.price.toString();
    nftMetadata.mintedAt = Number((new Date().getTime() / 1000).toFixed(0));
    nftMetadata.mintedBy = walletAddress;

    for (const choice in selectedItems) {
      const { category } = selectedItems[choice];
      if (category === "background") {
        attributes.push({
          rarityType: category,
          value: selectedItems[choice].colorHexCode,
          id: selectedItems[choice].backgroundId,
          trait_type: category,
        });
      } else if (category === "pattern") {
        attributes.push({
          rarityType: category,
          value: selectedItems[choice].patternName,
          id: selectedItems[choice].patternId,
          trait_type: category,
        });
      } else if (category === "layerchoice") {
        attributes.push({
          rarityType: category,
          value: selectedItems[choice].layerURL,
          id: selectedItems[choice].layerChoiceId,
          trait_type: category,
        });
      } else if (category === "approvedname") {
        attributes.push({
          rarityType: category,
          value: nftMetadata.name,
          id: selectedItems[choice].approvedNameId,
          trait_type: category,
        });
      } else if (category === "distribution") {
        attributes.push({
          rarityType: category,
          value: selectedItems[choice].presetName,
          id: 0,
          trait_type: category,
        });
      }
    }

    nftMetadata.attributes = attributes;
    return {
      finalNFTimage: { data: getPreviewData(), file: uuidv4() },
      nftMetadata,
    };
  }, [selectedItems, themeConfig.APP_DESC, walletAddress, getPreviewData]);

  const constructMintRequestData = useCallback((metadata, res) => {
    return {
      campaignId: siteDetails.siteId,
      name: {
        id: selectedItems.name.approvedNameId,
        approvedName: selectedItems.name.approvedName,
        name: `${selectedItems.name.approvedName} #${selectedItems.name.nextNameNumber}`,
        number: selectedItems.name.nextNameNumber,
      },
      description: metadata.description,
      presetDistribution: metadata.presetDistribution,
      attributes: metadata.attributes,
      distribution: [
        {
          value: selectedItems.distribution.sponsorDistro,
          distCategory: "sponsor",
        },
        {
          value: selectedItems.distribution.beneficiaryDistro,
          distCategory: "beneficiary",
        },
        {
          value: selectedItems.distribution.artistDistro,
          distCategory: "artist",
        },
      ],
      tokenUri: res.tokenUri,
    };
  }, [siteDetails.siteId, selectedItems]);

 const setUserSelection = useCallback((selected) => {
  setSelectedItems(prevSelected => {
    // If the selected object contains a special flag, replace instead of merge
    if (selected.__replace) {
      // Remove the flag and use the rest as the new selection
      const { __replace, ...newSelection } = selected;
      return newSelection;
    }
    
    // Default behavior: merge with existing selection
    const _update = { ...prevSelected, ...selected };
    return _update;
  });
}, []);

  // Navigation functions - optimized
  const prevStep = useCallback(() => {
    if (
      artOptions[stepState - 1]?.category === "pattern" &&
      layerChoicesAreSingle !== layers.length
    ) {
      setDelayName("");
    }
    if (stepState !== 0) {
      // Use lighter state updates for better performance
      setData([]);
      setLoading(true);

      const targetStep = artOptions[stepState - 1]?.category === "distribution" &&
        presetDistribution.length === 1
        ? stepState - 2
        : stepState - 1;

      setStep(targetStep);
    }
  }, [artOptions, stepState, layerChoicesAreSingle, layers.length, presetDistribution.length]);

  const nextStep = useCallback(() => {
    if (artOptions[stepState + 1]?.category === "layerchoice") {
      setDelayName(constant.NAME);
    }

    if (validateSelection()) {
      if (stepState < artOptions.length - 1) {
        // Batch state updates for better performance
        const autoPreviewSteps = Object.keys(removedSteps).filter(
          (k) => removedSteps[k].step === stepState + 1 // && removedSteps[k].category !== "distribution"
        );

        if (autoPreviewSteps.length > 0) {
          // Prepare all state updates
          const updates = {
            data: [],
            loading: true,
          };

          // Apply updates
          setData(updates.data);
          setLoading(updates.loading);

          // Update selections
          const newSelections = { ...selectedItems };
          for (const selection of autoPreviewSteps) {
            newSelections[selection] = removedSteps[selection];
          }
          setSelectedItems(newSelections);

        }

        // Calculate next step
        let nextStepIndex = stepState + 1;
        if (
          artOptions[stepState + 1]?.category === "distribution" &&
          presetDistribution.length === 1
        ) {
          const pd = [
            presetDistribution[0].beneficiaryDistro,
            presetDistribution[0].sponsorDistro,
            presetDistribution[0].artistDistro,
            (100 - ((presetDistribution[0].beneficiaryDistro + (presetDistribution[0].sponsorDistro + constants.reddevcut) + (presetDistribution[0].artistDistro + constants.reddevcut)) / 100)) * 100
          ];
          setPieData(pd);
          setPreset(presetDistribution[0].presetName);
          nextStepIndex = stepState + 2;
        }

        setStep(nextStepIndex);
        window.scrollTo(0, 0);
      }
    } else {
      const errorMsg = error.MINT_MANDATORY_OPTION_SELECT;
      setError(errorMsg);
    }
  }, [artOptions, stepState, validateSelection, removedSteps, presetDistribution, constants.reddevcut, selectedItems, setError]);

  // Minting functions
  const mint = useCallback(async (e) => {
    setLoading(true);
    const _metadata = constructMintData();
    e.target.disabled = true;

    try {
      const response = await fetch(`${baseURL}/upload`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(_metadata),
      });

      if (response.status === 200) {
        const res = await response.json();
        const mintRequest = constructMintRequestData(_metadata.nftMetadata, res);
        setLoading(false);
        logInfo(mintRequest);
        setMr(mintRequest);
        setShowMintPopup(true);
        e.target.disabled = false;
      } else {
        setLoading(false);
        e.target.disabled = false;
        alert("internal server error");
      }
    } catch (err) {
      logError("Mint upload failed:", err);
      setLoading(false);
      e.target.disabled = false;
    }
  }, [constructMintData, constructMintRequestData]);

  const mintWithWallet = useCallback(async () => {
    try {
      const nameAvailable = await get(`/artCreationPack/${siteDetails.artCreationPackId}/approvednames`);
      const nextNameNumber = nameAvailable.find(
        (name) => name.approvedName === selectedItems.name.approvedName
      )?.nextNameNumber;

      if (selectedItems.name.nextNameNumber >= nextNameNumber) {
        logInfo("Minting with wallet", mr, 'price', selectedItems.background.price.toString());
        await mintToken(mr, selectedItems.background.price.toString());
      } else {
        setShowMintPopup(false);
        setError(error.ANE.replace("%3", `${selectedItems.name.approvedName} #${selectedItems.name.nextNameNumber}`));
      }
    } catch (err) {
      logError("Mint with wallet failed:", err);
      setShowMintPopup(false);
      const errorMsg = parseErrorMessage(err, themeConfig, 'mint');
      setError(errorMsg);
    }
  }, [siteDetails.artCreationPackId, selectedItems, mr, setError]);

  const mintToken = useCallback(async (mintRequest, price) => {
    let status = isWalletConnected;
    if (!isWalletConnected) {
      status = await connectWallet();
    }

    if (status) {
      setLoading(true);
      try {
        const message = await fundRaisin.mint(mintRequest, walletAddress, price);
        refreshNftData();
        logInfo(message);

        if (message === false) {
          setTimeout(() => {
            setLoading(false);
            setStep(0);
            navigate("/my");
          }, 5000);
        } else if (message.status > 0) {
          setLoading(false);
          setStep(0);
          navigate("/my");
        } else {
          setShowMintPopup(false);
          setError(error.UNKNOWN_ERROR);
        }
      } catch (err) {
        setShowMintPopup(false);
        logError(err);
        setLoading(false);
        const errorMsg = parseErrorMessage(err, themeConfig, 'mint');
        setError(errorMsg);
      }
    } else {
      setLoading(false);
    }
  }, [isWalletConnected, connectWallet, walletAddress, refreshNftData, navigate, themeConfig.APP_NFT_NAME, setError]);

  // Layer choice construction effect - fixed infinite loop
  useEffect(() => {
    const constructLayerChoices = async () => {
      if (layers.length === 0) {
        setDelayName(constant.NAME);
        return;
      }

      // Only run if we have a pattern selected and haven't processed these layers yet
      if (!selectedItems.pattern?.patternId) {
        return;
      }

      try {
        let start = stepState + 1;
        let layerChoiceCount = 0;
        const newRemovedSteps = {};
        const newArtOptions = [...artOptionstemp];

        // Process all layers in parallel for better performance
        const layerPromises = layers.map(async (layer, count) => {
          let layerChList = layerWiseChoice[layer.layerId];

          if (!layerChList) {
            layerChList = await get(
              `/artCreationPack/${siteDetails.artCreationPackId}/pattern/${selectedItems.pattern.patternId}/layer/${layer.layerId}/layerchoices`
            );

            setLayerWiseChoice((previous) => ({
              ...previous,
              [layer.layerId]: layerChList,
            }));
          }

          return { layer, layerChList, count };
        });

        const layerResults = await Promise.all(layerPromises);

        // Process results
        for (const { layer, layerChList, count } of layerResults) {
          if (layerChList.length === 1) {
            layerChoiceCount++;
            if (layerChList[0]) {
              newRemovedSteps[`layerchoice${count}`] = {
                ...layerChList[0],
                category: "layerchoice",
                step: start,
              };
            }
          } else {
            const dynamicLayerChoice = await addLayerChoiceDynamically(
              count,
              layers,
              setData,
              setLoading,
              layerChList
            );
            newArtOptions.splice(start, 0, dynamicLayerChoice);
            start++;

            setRarityData((obj) => ({
              ...obj,
              [dynamicLayerChoice.identifier]: {},
            }));
          }
        }

        // Update state only if there are changes
        if (Object.keys(newRemovedSteps).length > 0) {
          setRemovedSteps((prev) => ({ ...prev, ...newRemovedSteps }));
        }

        if (newArtOptions.length !== artOptionstemp.length) {
          setArtOptions(newArtOptions);
        }

        setLayerChoicesAreSingle(layerChoiceCount);

        if (layerChoiceCount > 0 && layerChoiceCount === layers.length) {
          setDelayName(constant.NAME);
        }

        setLoading(false);
      } catch (err) {
        logError("Failed to construct layer choices:", err);
        setLoading(false);
      }
    };

    constructLayerChoices();
  }, [layers, selectedItems.pattern?.patternId, siteDetails.artCreationPackId]); // Removed problematic dependencies

  // Separate effect for preset distribution
  useEffect(() => {
    if (presetDistribution.length === 1) {
      setRemovedSteps((prevSteps) => ({
        ...prevSteps,
        distribution: {
          presetName: presetDistribution[0].presetName,
          beneficiaryDistro: presetDistribution[0].beneficiaryDistro,
          artistDistro: presetDistribution[0].artistDistro,
          sponsorDistro: presetDistribution[0].sponsorDistro,
          sellerDistro: (100 - (presetDistribution[0].beneficiaryDistro + (presetDistribution[0].artistDistro + constants.reddevcut) + (presetDistribution[0].sponsorDistro + constants.reddevcut)) / 100) * 100,
          category: "distribution",
          actualName: presetDistribution[0].presetName,
          step: stepState + 1,
        },
      }));
    }
  }, [presetDistribution, constants.reddevcut, stepState]); // Only depend on length, not the full array

  // Layout effect for initial setup - fixed dependency
  useLayoutEffect(() => {
    window.scrollTo(0, 0);

    const initialize = async () => {
      if (!siteDetails.siteId || !siteDetails.artCreationPackId) {
        return;
      }

      setIsInitializing(true);
      try {
        // Parallel loading of initial data
        await Promise.allSettled([
          checkSiteActive(),
          getRarity(),
          getPresetDistributions()
        ]);
      } catch (err) {
        logError("Failed to initialize app:", err);
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, [siteDetails.siteId, siteDetails.artCreationPackId]); // Use direct dependencies instead of function

  // Effect for step data loading
  useEffect(() => {
    console.log('Step changed to:', stepState, 'artOptions length:', artOptions.length);

    if (artOptions[stepState]) {
      artOptions[stepState].getData();
      setTitle(artOptions[stepState].title);
      setIdentifier(artOptions[stepState].identifier);
      setCategory(artOptions[stepState].category);
    }
  }, [stepState]); // Only depend on stepState

  // Effect for preview data - use ref to check if preview is needed
  useEffect(() => {
    console.log('Preview effect triggered - selectedItems keys:', Object.keys(selectedItems), 'stepState:', stepState);

    const shouldPreview = () => {
      const currentOptions = artOptionsRef.current;
      const needsPreview = currentOptions?.[stepState]?.preview;
      console.log('Should preview?', needsPreview, 'current step preview:', currentOptions?.[stepState]?.preview);
      return needsPreview;
    };

    const previewData = async () => {
      console.log('Starting preview...');
      if (shouldPreview()) {
        await preview();
      } else {
        console.log('No preview needed, setting loading false');
        setLoading(false);
      }
    };

    // Only preview if we have selections and need preview
    if (Object.keys(selectedItems).length > 0 && shouldPreview()) {
      console.log('Scheduling preview with debounce');
      // Debounce preview calls
      const timeoutId = setTimeout(previewData, 200);
      return () => {
        console.log('Clearing preview timeout');
        clearTimeout(timeoutId);
      };
    } else {
      console.log('No selections or preview not needed');
      setLoading(false);
    }
  }, [selectedItems, preview]);

  // Effect for layer updates
  useEffect(() => {
    const updateLayer = async () => {
      try {
        const layersData = await get(
          `/artCreationPack/${siteDetails.artCreationPackId}/pattern/${selectedItems.pattern.patternId}/layers`
        );
        setLayers(layersData);
      } catch (err) {
        logError("Failed to update layers:", err);
      }
    };

    if (selectedItems?.pattern?.patternId) {
      setLoading(true);
      updateLayer();
      // Reset artOptions to original template
      setArtOptions([...artOptionstemp]);
    }
  }, [selectedItems?.pattern?.patternId, siteDetails.artCreationPackId]); // Removed artOptionstemp from dependencies

  // Render functions
  const renderMintPopup = useCallback(() => (
    <Modal show={showMintPopup}>
      <Popup type="mint">
        <MintPopup
          mintData={mr}
          price={selectedItems?.background?.price.toString()}
          onWalletClick={mintWithWallet}
          onClose={() => setShowMintPopup(!showMintPopup)}
          onError={() => {
            setShowMintPopup(false);
            setError(error.ANE.replace("%3", `${selectedItems.name.approvedName} #${selectedItems.name.nextNameNumber}`));
          }}
        />
      </Popup>
    </Modal>
  ), [showMintPopup, mr, selectedItems, mintWithWallet, setError]);

  const renderStep = useCallback(() => {
    const currentWidget = artOptions[stepState]?.widget;

    switch (currentWidget) {
      case "catalogue":
        return (
          <Catalogue
            category={category}
            identifier={identifier}
            data={data}
            setUserSelection={setUserSelection}
            selectedItems={selectedItems}
            title={title}
            rarityList={rarityList}
            rarityData={rarityData}
            setRarityData={setRarityData}
          />
        );
      case "name":
        return (
          <Name
            nameList={names}
            setUserSelection={setUserSelection}
            selectedItems={selectedItems}
            title={title}
            rarityList={rarityList.filter((i) => i.rarityType === "approvedname")}
            webPrimaryColor={themeConfig.PRIMARY}
          />
        );
      case "distribution":
        return (
          <Distribution
            setUserSelection={setUserSelection}
            distribution={presetDistribution}
            sliders={sliders}
            pieData={pieData}
            setPieData={setPieData}
            preset={preset}
            setPreset={setPreset}
            showPreset={showPreset}
            setShowPreset={setShowPreset}
            setDefaultSlider={setDefaultSlider}
            reddevcut={constants.reddevcut}
            selectedItems={selectedItems}
          />
        );
      case "final":
        return (
          <FinalCheck
            selectedItems={selectedItems}
            setUserSelection={setUserSelection}
            artOptions={artOptions}
            sliders={sliders}
            preset={preset}
            title={title}
            rarityData={rarityData}
            previewLink={previewLink}
            reddevcut={constants.reddevcut}
          />
        );
      default:
        return null;
    }
  }, [
    artOptions, stepState, category, identifier, data, setUserSelection,
    selectedItems, title, rarityList, rarityData, setRarityData, names,
    themeConfig.PRIMARY, presetDistribution, sliders, pieData, setPieData,
    preset, setPreset, showPreset, setShowPreset, setDefaultSlider,
    constants.reddevcut, previewLink
  ]);

  const renderStepPreview = useCallback(() => {
    const currentWidget = artOptions[stepState]?.widget;

    if (["catalogue", "name", "final"].includes(currentWidget)) {
      return (
        <NftPreview
          loader={loader}
          input={selectedItems}
          previewLink={previewLink}
          pieData={pieData}
          preset={preset}
          widget={currentWidget}
        />
      );
    } else if (["distribution"].includes(currentWidget)) {
      return (
        <PieDist
          pieData={pieData}
          preset={preset}
          sliders={sliders}
          previewLink={previewLink}
          widget={currentWidget}
        />
      );
    }
    return null;
  }, [artOptions, stepState, loader, selectedItems, previewLink, pieData, preset, sliders]);

  // Memoized footer props
  const footerProps = useMemo(() => ({
    nextHover: `Please select a ${title} first`,
    nextButton: stepState !== artOptions.length - 1 &&
      artOptions[stepState + 1]?.category === "distribution" &&
      presetDistribution.length === 1
      ? artOptions[stepState + 2]?.navButton
      : artOptions[stepState + 1]?.navButton || delayedName,
    backButton: stepState !== 0 &&
      artOptions[stepState - 1]?.category === "distribution" &&
      presetDistribution.length === 1
      ? artOptions[stepState - 2]?.navButton
      : artOptions[stepState - 1]?.navButton || delayedName,
    stepTotal: artOptions.length - 1,
    stepState,
    validateSelection,
    input: selectedItems,
    Previous: prevStep,
    Continue: nextStep,
    MintNft: mint,
    webPrimaryColor: themeConfig.PRIMARY,
  }), [
    title, stepState, artOptions, presetDistribution.length, delayedName,
    validateSelection, selectedItems, prevStep, nextStep, mint, themeConfig.PRIMARY
  ]);

  // Early return if no art options
  if (!artOptions || artOptions.length === 0) {
    return <Spinner spinner={true} />;
  }

  return (
    <section id="mint" className="page-settings">
      {showMintPopup && renderMintPopup()}
      {isInitializing ? (
        <div className="mint__initializing">
          <h2>Preparing your minting experience...</h2>
          <p>Loading art options and configurations</p>
        </div>
      ) : (
        <>
          <h1 className="mint__title title-styles-nocaptial">
            Buy New {themeConfig.APP_NFT_NAME}
          </h1>
          <Spinner spinner={isLoading} />
          <div className="mint__box">
            <div className="mint__box-wrapper">
              <div className="mint__steps">
                <div className="mint__steps-left">
                  {renderStep()}
                </div>
                <div className="mint__steps-right">
                  {renderStepPreview()}
                </div>
              </div>
              <MintFooter {...footerProps} />
            </div>
          </div>

          <div className="mint__box-mobile">
            <div className="mint__steps-mobile">
              <div className="mint__steps-top">
                {renderStepPreview()}
              </div>
              <div className="mint__steps-bottom">
                {renderStep()}
              </div>
            </div>
            <MintFooter
              {...footerProps}
              nextButton={artOptions[stepState + 1]?.navButton || delayedName}
              backButton={artOptions[stepState - 1]?.navButton || delayedName}
            />
          </div>
        </>
      )}
    </section>
  );
});

// Add comprehensive cleanup effect
const MintWithCleanup = () => {
  const mountedRef = useRef(true);
  const timeoutRefs = useRef([]);

  // Cleanup function to clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutRefs.current.forEach(timeoutId => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    timeoutRefs.current = [];
  }, []);

  // Track timeouts for cleanup
  const addTimeout = useCallback((timeoutId) => {
    timeoutRefs.current.push(timeoutId);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  return <Mint />;
};

// Array prototype extension (keeping original functionality)
Array.prototype.insert = function (index, ...items) {
  this.splice(index, 0, ...items);
};

Mint.displayName = "Mint";
MintWithCleanup.displayName = "MintWithCleanup";

export default MintWithCleanup;
